import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { DeletePinControls } from "@/components/DeletePinControls";
import { Plus, Link2, RefreshCw, KeyRound, ExternalLink, FileDown, Eye, Check, Edit, Trash2, AlertTriangle, Info } from "lucide-react";

export default function Franchises() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [list, setList] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState({ name: "", city: "", contact_name: "", contact_phone: "", contact_email: "" });
  const [pinTarget, setPinTarget] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null); // franchise being deleted
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingBusy, setDeletingBusy] = useState(false);

  const [copiedId, setCopiedId] = useState(null);
  const [copyFallback, setCopyFallback] = useState(null);

  const load = useCallback(() => api.get("/franchises").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!creating.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/franchises", creating);
      toast.success("Franchise created");
      setShowCreate(false);
      setCreating({ name: "", city: "", contact_name: "", contact_phone: "", contact_email: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    }
  };

  const regenerate = async (id) => {
    if (!window.confirm("Regenerate link? The old link will stop working.")) return;
    await api.post(`/franchises/${id}/regenerate-link`);
    toast.success("Link regenerated");
    load();
  };

  const setPin = async () => {
    if (!/^\d{4}$/.test(newPin)) return toast.error("PIN must be 4 digits");
    try {
      await api.post(`/franchises/${pinTarget.id}/set-pin`, { pin: newPin });
      toast.success("PIN updated");
      setPinTarget(null);
      setNewPin("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleActive = async (f) => {
    await api.patch(`/franchises/${f.id}`, { is_active: !f.is_active });
    load();
  };

  const buildLink = (token) => `${window.location.origin}/franchise/${token}`;

  const copy = async (f) => {
    const link = buildLink(f.shareable_link_token);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(f.id);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedId((c) => (c === f.id ? null : c)), 2000);
    } catch (e) {
      // Fallback: show modal with the link selectable
      setCopyFallback(link);
    }
  };

  const saveEdit = async () => {
    if (!editing.name?.trim()) return toast.error("Name is required");
    try {
      await api.patch(`/franchises/${editing.id}`, {
        name: editing.name, city: editing.city,
        contact_name: editing.contact_name, contact_phone: editing.contact_phone, contact_email: editing.contact_email
      });
      toast.success("Franchise details updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const openDelete = async (f) => {
    setDeleting(f);
    setDeleteConfirmText("");
    setDeletionImpact(null);
    try {
      const { data } = await api.get(`/franchises/${f.id}/deletion-impact`);
      setDeletionImpact(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to fetch impact");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    if (deleteConfirmText.trim() !== deleting.name) {
      return toast.error("Franchise name does not match");
    }
    setDeletingBusy(true);
    try {
      const { data } = await api.delete(`/franchises/${deleting.id}`);
      toast.success(`Deleted ${deleting.name}. ${data.products_reassigned || 0} products moved to Unassigned.`);
      setDeleting(null);
      setDeletionImpact(null);
      setDeleteConfirmText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setDeletingBusy(false);
    }
  };


  const downloadReport = (id, name) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/franchise/${id}/excel`;
    const tok = localStorage.getItem("access_token");
    fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u;
        a.download = `${name.replace(/\s/g, "_")}_report.xlsx`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <div className="space-y-8" data-testid="franchises-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Network</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Franchises</h1>
          <p className="text-slate-500 mt-2">Manage partners, share access links, and set PIN protection.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white" data-testid="create-franchise-btn">
            <Plus className="w-4 h-4 mr-2" /> Add franchise
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="luxe-card p-4 bg-amber-50/50 border-l-4 border-amber-300 flex items-start gap-3" data-testid="franchises-readonly-banner">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">Viewing franchise list (read-only). Contact a Master Admin to modify franchise details or links.</p>
        </div>
      )}

      <div className="grid gap-6">
        {list === null && Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-32" />)}
        {list && list.map((f) => {
          const link = buildLink(f.shareable_link_token);
          return (
            <div key={f.id} className="luxe-card p-6" data-testid={`franchise-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-2xl text-[#0D1B2A]">{f.name}</h3>
                    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${f.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {f.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{f.city} · {f.contact_name} · {f.contact_phone}</div>
                  <div className="text-xs text-slate-400 mt-2 font-mono break-all">{link}</div>
                  <div className="text-xs text-slate-500 mt-2">PIN: <span className="font-mono text-[#0D1B2A]">{f.owner_pin_plain || "—"}</span> · {f.product_count} products</div>
                  <div className="mt-2" data-testid={`franchise-${f.id}-delete-pin-row`}>
                    <DeletePinControls
                      entity={f}
                      basePath={`/franchises/${f.id}`}
                      canEdit={canEdit}
                      onChanged={load}
                      testidPrefix={`franchise-${f.id}`}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canEdit && (
                    <button onClick={() => setEditing({ ...f })} data-testid={`edit-franchise-${f.id}`}
                      className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]">
                      <Edit className="w-4 h-4" /> Edit
                    </button>
                  )}
                  <button onClick={() => copy(f)} data-testid={`copy-link-${f.id}`}
                    className={`px-3 py-2 rounded-md border text-sm flex items-center gap-2 transition-colors ${
                      copiedId === f.id
                        ? "border-emerald-500 text-emerald-600 bg-emerald-50"
                        : "border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
                    }`}>
                    {copiedId === f.id ? <><Check className="w-4 h-4" /> Copied!</> : <><Link2 className="w-4 h-4" /> Copy</>}
                  </button>
                  <button onClick={() => window.open(link, "_blank")} className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]">
                    <Eye className="w-4 h-4" /> Preview
                  </button>
                  {canEdit && (
                    <button onClick={() => regenerate(f.id)} data-testid={`regenerate-${f.id}`} className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]">
                      <RefreshCw className="w-4 h-4" /> Regenerate
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => { setPinTarget(f); setNewPin(""); }} data-testid={`set-pin-${f.id}`} className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]">
                      <KeyRound className="w-4 h-4" /> PIN
                    </button>
                  )}
                  <button onClick={() => downloadReport(f.id, f.name)} data-testid={`export-${f.id}`} className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]">
                    <FileDown className="w-4 h-4" /> Excel
                  </button>
                  {canEdit && (
                    <button onClick={() => toggleActive(f)} className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm hover:bg-[#FAF8F3]">
                      {f.is_active ? "Deactivate" : "Activate"}
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => openDelete(f)} data-testid={`delete-franchise-${f.id}`}
                      className="px-3 py-2 rounded-md border border-red-300 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New Franchise</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="create-name" />
            <Input placeholder="City" value={creating.city} onChange={(e) => setCreating({ ...creating, city: e.target.value })} data-testid="create-city" />
            <Input placeholder="Contact name" value={creating.contact_name} onChange={(e) => setCreating({ ...creating, contact_name: e.target.value })} />
            <Input placeholder="Phone" value={creating.contact_phone} onChange={(e) => setCreating({ ...creating, contact_phone: e.target.value })} />
            <Input placeholder="Email" value={creating.contact_email} onChange={(e) => setCreating({ ...creating, contact_email: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={create} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="create-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinTarget} onOpenChange={(o) => { if (!o) setPinTarget(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Set Owner PIN</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">4-digit PIN used by the franchise owner to access pricing on their shared link.</p>
          <Input
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            className="text-2xl tracking-[0.5em] text-center h-14"
            data-testid="pin-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinTarget(null)}>Cancel</Button>
            <Button onClick={setPin} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="pin-save">Save PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!copyFallback} onOpenChange={(o) => { if (!o) setCopyFallback(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Copy this link</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Your browser blocked automatic copy. Select the link below and copy manually.</p>
          <Input value={copyFallback || ""} readOnly className="font-mono text-xs" data-testid="copy-fallback-input"
            onFocus={(e) => e.target.select()} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-white" data-testid="edit-franchise-modal">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit franchise details</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Franchise name</label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-franchise-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">City</label>
                <Input value={editing.city || ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid="edit-franchise-city" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Contact name</label>
                <Input value={editing.contact_name || ""} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Phone</label>
                <Input value={editing.contact_phone || ""} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Email</label>
                <Input type="email" value={editing.contact_email || ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#0D1B2A]/5">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Shareable link</label>
                  <Input value={`${window.location.origin}/franchise/${editing.shareable_link_token}`} readOnly className="text-xs font-mono bg-slate-50" />
                  <p className="text-[10px] text-slate-400 mt-1">Use &quot;Regenerate&quot; button to rotate.</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Owner PIN</label>
                  <Input value="••••" readOnly className="bg-slate-50" />
                  <p className="text-[10px] text-slate-400 mt-1">Use &quot;PIN&quot; button to reset.</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="edit-franchise-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o && !deletingBusy) { setDeleting(null); setDeletionImpact(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="bg-white max-w-lg" data-testid="delete-franchise-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-6 h-6" /> Delete franchise
            </DialogTitle>
          </DialogHeader>
          {deleting && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                You are about to permanently delete <span className="font-semibold text-[#0D1B2A]">{deleting.name}</span>.
                This action cannot be undone.
              </p>
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm space-y-2">
                <div className="text-red-800 font-medium mb-1">What will happen</div>
                {deletionImpact ? (
                  <ul className="text-red-700 list-disc pl-5 space-y-1">
                    <li>
                      <span className="font-mono font-semibold">{deletionImpact.product_count}</span> product{deletionImpact.product_count === 1 ? "" : "s"} will be moved to <span className="font-semibold">Unassigned</span>.
                    </li>
                    <li>
                      <span className="font-mono font-semibold">{deletionImpact.user_count}</span> user account{deletionImpact.user_count === 1 ? "" : "s"} will be deactivated.
                    </li>
                    <li>The franchise&apos;s shareable link will stop working.</li>
                  </ul>
                ) : (
                  <div className="text-red-700">Loading impact…</div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                  Type <span className="font-mono text-red-700">{deleting.name}</span> to confirm
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleting.name}
                  data-testid="delete-confirm-input"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleting(null); setDeletionImpact(null); setDeleteConfirmText(""); }} disabled={deletingBusy}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deletingBusy || !deleting || deleteConfirmText.trim() !== deleting?.name}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="delete-confirm-btn"
            >
              {deletingBusy ? "Deleting…" : "Delete franchise"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
